// =============================================================================
// Optim TURPE V2 — CYCLE DE VIE ET HISTORIQUE (DEV-1913, A12, §7 du contrat).
//
// Ce que le client doit vivre : il retrouve les études de son compteur avec leur
// date de calcul, il rouvre une étude précédente pour lire ce qui avait été
// servi, il peut en supprimer une. Un rejeu ne réécrit plus l'étude en place :
// il en crée une neuve, chaînée, et l'ancienne reste lisible, marquée
// « remplacée le … ».
//
// Les gestes « rejouer », « rétablir » et « supprimer » passent par les routes
// servies par le lot DEV-2065 (courtage#236, façades api-metier) : DEV-2150.
//
// Un LANCEMENT depuis la page crée DEUX études, une par scénario de l'arbitrage
// des dépassements (sans et avec, `allow_cmdps`). Relevé en lecture seule le
// 25/09/2026 sur la base de production : les « doublons » de l'historique
// signalés par DEV-2156 sont exactement ces deux scénarios, créés à quelques
// millisecondes d'écart. L'historique les regroupe en une ligne (DEV-2156).
//
// Le repli : une étude antérieure au lot ne porte pas de bloc `cycle`. Elle
// s'affiche alors comme une étude isolée, datée par `created_at`, sans rang ni
// chaîne — ce qui est exactement ce qu'elle est.
// =============================================================================

import type {
    OptimTurpeV2Conduite,
    OptimTurpeV2Etude,
} from "@modules/courtage/types/optim-turpe-v2"
import { SEUIL_ETUDE_ABANDONNEE_MS } from "@modules/courtage/utils/optim-turpe-v2.relecture"
import type { OptimTurpeV2RunBody } from "@modules/courtage/services/optim-turpe-v2.service"
import { formatMontantSigne } from "@modules/courtage/utils/optim-turpe-v2.montant"
import { refusConnuDe } from "@modules/courtage/utils/optim-turpe-v2.echec"
import {
    conseilEtabli,
    scenarioRetenuServi,
} from "@modules/courtage/utils/optim-turpe-v2.arbitrage"

export type CycleLu = {
    idCycle: string | null
    rang: number | null
    origine: "creation" | "rejeu" | "scenario_libre" | null
    idPrecedente: string | null
    /** Cette étude est-elle celle que la page ouvre par défaut ? `null` = non servi. */
    courante: boolean | null
    remplaceePar: string | null
    remplaceeLe: string | null
    supprimeeLe: string | null
    /** Vrai quand le document porte le bloc `cycle`. */
    servi: boolean
}

/** Le cycle servi, ou son repli « étude isolée » sur un document antérieur au lot. */
export function lireCycle(etude: OptimTurpeV2Etude): CycleLu {
    const cycle = etude.cycle ?? null
    return {
        idCycle: cycle?.id_cycle ?? null,
        rang: typeof cycle?.rang === "number" ? cycle.rang : null,
        origine: cycle?.origine ?? null,
        idPrecedente: cycle?.id_precedente ?? null,
        courante: typeof cycle?.courante === "boolean" ? cycle.courante : null,
        remplaceePar: cycle?.remplacee_par ?? null,
        remplaceeLe: cycle?.remplacee_le ?? null,
        supprimeeLe: cycle?.supprimee_le ?? null,
        servi: cycle !== null && cycle !== undefined,
    }
}

/** L'étude a-t-elle été remplacée par un rejeu ? Se dit sur la ligne de l'étude. */
export function estRemplacee(etude: OptimTurpeV2Etude): boolean {
    return lireCycle(etude).remplaceePar !== null
}

/** L'étude est-elle supprimée logiquement (Q-CIBLE-6) ? */
export function estSupprimee(etude: OptimTurpeV2Etude): boolean {
    return lireCycle(etude).supprimeeLe !== null
}

export type ConduiteLue = {
    /** `scenario_libre` = `meilleure` porte la structure IMPOSÉE, pas l'optimum. */
    mode: "optimisation" | "scenario_libre" | null
    /** Vrai quand le moteur sert `restitution.conduite.mode`. */
    servie: boolean
    cibleSaisie: OptimTurpeV2Conduite["cible_saisie"]
    /** Champs repris de l'actuelle parce que l'appelant ne les avait pas donnés. */
    cibleCompletee: string[]
    /** « Vous avez changé HPH de 120 à 100 kVA » — servi par le moteur. */
    ecartsALActuelle: NonNullable<OptimTurpeV2Conduite["ecarts_a_l_actuelle"]>
    /** L'étude d'optimisation du même cycle, pour la comparaison côte à côte. */
    idEtudeRecommandee: string | null
}

/**
 * La conduite de l'étude (§5).
 *
 * Le défaut corrigé : rien dans `outputs` ne disait qu'une « meilleure » était en
 * réalité un scénario imposé par l'utilisateur — ni l'écran ni un client du
 * webhook ne pouvaient le savoir. Le mode est désormais servi ; le repli lit
 * `inputs.optimisation.mode`, qui est ce que l'appelant a demandé, et le dit.
 */
export function lireConduite(etude: OptimTurpeV2Etude): ConduiteLue {
    const conduite = etude.conduite ?? null
    const modeServi = conduite?.mode ?? null
    const modeDemande =
        etude.inputs?.optimisation?.mode === "price_only"
            ? ("scenario_libre" as const)
            : etude.inputs?.optimisation?.mode === "optimiser"
              ? ("optimisation" as const)
              : null
    return {
        mode: modeServi ?? modeDemande,
        servie: modeServi !== null,
        cibleSaisie:
            conduite?.cible_saisie ?? etude.inputs?.optimisation?.cible ?? null,
        cibleCompletee: conduite?.cible_completee ?? [],
        ecartsALActuelle: conduite?.ecarts_a_l_actuelle ?? [],
        idEtudeRecommandee: conduite?.id_etude_recommandee ?? null,
    }
}

/** L'étude porte-t-elle un scénario libre (structure imposée, chiffrée telle quelle) ? */
export function estScenarioLibre(etude: OptimTurpeV2Etude): boolean {
    return lireConduite(etude).mode === "scenario_libre"
}

/**
 * Trie les études d'un historique de la plus récente à la plus ancienne.
 *
 * L'instant du CALCUL fait foi quand il est servi (`outputs.calcul.calcule_le`) :
 * `updated_at` date aussi les rejeux, et une étude rejouée remonterait alors en
 * tête sans avoir été recalculée. Une étude sans date reste en fin de liste
 * plutôt que d'être datée d'aujourd'hui.
 */
export function trierParDateDeCalcul(
    etudes: OptimTurpeV2Etude[]
): OptimTurpeV2Etude[] {
    const instant = (etude: OptimTurpeV2Etude): number => {
        // Refus du 20/10/2026 rangé en bas de la liste : `created_at` est
        // servi SANS fuseau (datetime naïf relu de Mongo) quand `calcule_le`
        // porte « +00:00 ». Lu par `Date.parse`, il passait pour une heure
        // locale, deux heures trop tôt à Paris : un refus, qui n'a pas de
        // calcul, se rangeait derrière les études calculées entre-temps.
        const date =
            etude.calcul?.calcule_le ?? etude.created_at ?? etude.updated_at
        return lireInstant(date ?? null) ?? -Infinity
    }
    return [...etudes].sort((a, b) => instant(b) - instant(a))
}

/** La date de calcul à afficher sur la ligne d'une étude, ou `null`. */
export function dateDeCalcul(etude: OptimTurpeV2Etude): string | null {
    return etude.calcul?.calcule_le ?? etude.created_at ?? null
}

/**
 * La date d'un LANCEMENT, vue depuis l'étude affichée (revue témoin du
 * 15/10/2026, D19 : 11:20 dans la barre collante, 11:19 dans l'historique).
 * Une seule date par lancement, la règle de `construireLancement` : la date
 * de calcul de son étude principale, la jambe « sans dépassements » quand la
 * paire est là, sinon l'autre jambe. Une étude hors de la paire (étude saisie,
 * refus) garde sa propre date.
 */
export function dateDuLancementAffiche(
    etude: OptimTurpeV2Etude,
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): string | null {
    const id = etude._id ?? null
    const dansLaPaire = id !== null && (sans?._id === id || avec?._id === id)
    const principale = dansLaPaire ? (sans ?? avec ?? etude) : etude
    return dateDeCalcul(principale)
}

/** Écart maximal entre les deux scénarios d'un même lancement (ils partent ensemble). */
export const ECART_MAX_SCENARIOS_MS = 60_000

/**
 * Lit un horodatage servi. Une date sans fuseau (forme de la base) est en UTC :
 * la lire en heure locale décalerait l'heure affichée d'une à deux heures.
 */
export function lireInstant(date: string | null | undefined): number | null {
    if (!date) return null
    const brut = date.trim().replace(" ", "T")
    const avecFuseau = /([zZ]|[+-]\d{2}:?\d{2})$/.test(brut) ? brut : `${brut}Z`
    // Les microsecondes servies par Python ne sont pas lues partout : on garde
    // les millisecondes.
    const normalise = avecFuseau.replace(/(\.\d{3})\d+/, "$1")
    const t = Date.parse(normalise)
    return Number.isNaN(t) ? null : t
}

/** Un lancement : les études parties ensemble, une par scénario de l'arbitrage. */
export type LancementLu = {
    /** Clé stable de la ligne : l'identifiant de l'étude la plus récente. */
    cle: string
    /** Identifiants des études du lancement, « sans » d'abord. */
    ids: string[]
    etudes: OptimTurpeV2Etude[]
    sans: OptimTurpeV2Etude | null
    avec: OptimTurpeV2Etude | null
    /** L'étude qui porte la ligne (date, état, cycle) : « sans » si elle existe. */
    principale: OptimTurpeV2Etude
    date: string | null
}

function scenarioDe(etude: OptimTurpeV2Etude): boolean | null {
    const allow = etude.inputs?.optimisation?.allow_cmdps
    return typeof allow === "boolean" ? allow : null
}

function memePeriodeEtSource(
    a: OptimTurpeV2Etude,
    b: OptimTurpeV2Etude
): boolean {
    return (
        (a.inputs?.source?._id_source ?? null) ===
            (b.inputs?.source?._id_source ?? null) &&
        (a.inputs?.data?.date_from ?? null) ===
            (b.inputs?.data?.date_from ?? null) &&
        (a.inputs?.data?.date_to ?? null) === (b.inputs?.data?.date_to ?? null)
    )
}

/**
 * Les entrées d'une étude HORS `allow_cmdps`, sous une forme comparable : les
 * deux études d'un lancement partent du même corps, au scénario près
 * (`withAllow` du magasin). Clés triées : l'ordre servi ne compte pas.
 */
function entreesHorsScenario(etude: OptimTurpeV2Etude): string {
    const inputs = etude.inputs
    const { allow_cmdps: _scenario, ...optimisation } = (inputs?.optimisation ??
        {}) as Record<string, unknown>
    const trie = (valeur: unknown): unknown => {
        if (Array.isArray(valeur)) return valeur.map(trie)
        if (valeur && typeof valeur === "object") {
            // Une clé nulle vaut une clé absente (comme `_cle` du moteur).
            const objet = valeur as Record<string, unknown>
            return Object.fromEntries(
                Object.keys(objet)
                    .filter((k) => objet[k] !== null && objet[k] !== undefined)
                    .sort()
                    .map((k) => [k, trie(objet[k])])
            )
        }
        return valeur ?? null
    }
    return JSON.stringify(
        trie({
            source: inputs?.source?._id_source ?? null,
            date_from: inputs?.data?.date_from ?? null,
            date_to: inputs?.data?.date_to ?? null,
            optimisation,
            structure_tarifaire: inputs?.structure_tarifaire ?? null,
            compteur: inputs?.compteur ?? null,
            comptage: inputs?.comptage ?? null,
        })
    )
}

function instantDe(etude: OptimTurpeV2Etude): number | null {
    return lireInstant(dateDeCalcul(etude) ?? etude.updated_at ?? null)
}

/**
 * La jumelle que le MOTEUR a liée à cette étude (`outputs.arbitrage.jumelle._id`,
 * courtage `arbitrage.py` : mêmes entrées hors `allow_cmdps`, scénario opposé,
 * créées à moins de 15 minutes, la plus proche). Servie sur une paire aboutie
 * seulement ; `null` sinon.
 */
function idJumelleServie(etude: OptimTurpeV2Etude): string | null {
    const id = etude.arbitrage?.jumelle?._id
    return typeof id === "string" && id !== "" ? id : null
}

/**
 * L'instant de CRÉATION de l'étude : c'est lui qui rapproche les deux scénarios
 * d'un lancement (deux POST partis ensemble). La date de calcul ne le peut
 * pas : les deux calculs passent l'un après l'autre et s'écartent de la durée
 * d'un calcul (finitions 2 du 07/10/2026 : « Études de ce compteur » comptait
 * la paire pour deux). Repli : la mise en file, puis la date de calcul.
 */
function instantDeCreation(etude: OptimTurpeV2Etude): number | null {
    return (
        lireInstant(etude.created_at ?? null) ??
        lireInstant(etude.reprise?.enfilee_at ?? null) ??
        instantDe(etude)
    )
}

function construireLancement(etudes: OptimTurpeV2Etude[]): LancementLu {
    const sans = etudes.find((e) => scenarioDe(e) === false) ?? null
    const avec = etudes.find((e) => scenarioDe(e) === true) ?? null
    const ordonnees = [sans, avec, ...etudes].filter(
        (e, i, arr): e is OptimTurpeV2Etude =>
            e !== null && arr.indexOf(e) === i
    )
    const principale = ordonnees[0]
    return {
        cle: String(principale._id ?? ""),
        ids: ordonnees
            .map((e) => String(e._id ?? ""))
            .filter((id) => id !== ""),
        etudes: ordonnees,
        sans,
        avec,
        principale,
        date: dateDeCalcul(principale),
    }
}

/**
 * Regroupe les études en LANCEMENTS, du plus récent au plus ancien.
 *
 * Le lien SERVI d'abord : la jumelle que le moteur a posée dans l'arbitrage
 * d'une paire aboutie (`arbitrage.jumelle._id`), quand elle est dans la liste.
 * Sinon (refus, calcul en cours, étude sans arbitrage), deux études forment un
 * lancement quand elles portent les deux scénarios opposés, les MÊMES entrées
 * hors `allow_cmdps` (source, période, prudence, structure, compteur,
 * comptage) et qu'elles ont été CRÉÉES à moins d'une minute d'écart ; parmi
 * plusieurs candidates, la plus proche en création. Tout le reste reste une
 * étude isolée (refus connu, scénario saisi) : on n'apparie jamais au doute.
 */
export function grouperParLancement(
    etudes: OptimTurpeV2Etude[]
): LancementLu[] {
    const triees = trierParDateDeCalcul(etudes)
    const prises = new Set<OptimTurpeV2Etude>()
    const lancements: LancementLu[] = []
    for (const etude of triees) {
        if (prises.has(etude)) continue
        prises.add(etude)
        const scenario = scenarioDe(etude)
        const t = instantDeCreation(etude)
        const idEtude = String(etude._id ?? "")
        const idServi = idJumelleServie(etude)
        let jumelle: OptimTurpeV2Etude | undefined =
            scenario === null
                ? undefined
                : triees.find(
                      (autre) =>
                          !prises.has(autre) &&
                          scenarioDe(autre) !== null &&
                          scenarioDe(autre) !== scenario &&
                          ((idServi !== null &&
                              String(autre._id ?? "") === idServi) ||
                              (idEtude !== "" &&
                                  idJumelleServie(autre) === idEtude))
                  )
        let ecartJumelle = Infinity
        if (!jumelle && scenario !== null && t !== null) {
            const entrees = entreesHorsScenario(etude)
            for (const autre of triees) {
                if (prises.has(autre)) continue
                const s = scenarioDe(autre)
                const ta = instantDeCreation(autre)
                if (s === null || s === scenario || ta === null) continue
                const ecart = Math.abs(ta - t)
                if (ecart > ECART_MAX_SCENARIOS_MS || ecart >= ecartJumelle)
                    continue
                if (entreesHorsScenario(autre) !== entrees) continue
                jumelle = autre
                ecartJumelle = ecart
            }
        }
        if (jumelle) prises.add(jumelle)
        lancements.push(
            construireLancement(jumelle ? [etude, jumelle] : [etude])
        )
    }
    return lancements
}

/** « +2 820 € » ; `null` quand le montant n'est pas servi (jamais un 0). */
function montantDeLaLigne(etude: OptimTurpeV2Etude | null): string | null {
    if (!etude) return null
    const nette = etude.economie?.nette_eur
    if (etude.economie?.montant_servi === false) return null
    // Signé à la convention de la page (revue du 08/10, R1).
    return formatMontantSigne(nette)
}

/**
 * Le résultat d'un lancement sur sa ligne d'historique, UN seul montant (R21,
 * A2 du 14/10/2026 : « +2 500 € · +2 500 € » se redisait) : celui du scénario
 * recommandé de la paire, servi par le moteur ; une étude seule, son montant.
 * Un montant non servi rend `null`, jamais 0. La ligne ne nomme pas son
 * scénario : sans conseil établi, elle écrit « conseil non établi », sans
 * montant (décision du pilote du 14/10/2026, à la place de la ligne vide),
 * plutôt que le montant de l'autre scénario ; quand le montant du scénario
 * conseillé n'est pas servi, elle n'écrit rien (revue du lot 3).
 */
export const CONSEIL_NON_ETABLI = "conseil non établi"

export function resultatsDuLancement(lancement: LancementLu): string | null {
    const etude = etudeDuResultat(lancement)
    if (etude) return montantDeLaLigne(etude)
    // Paire sans conseil établi. Rien à départager (aucun montant servi,
    // échec) : rien à dire.
    const unMontant =
        montantDeLaLigne(lancement.sans) !== null ||
        montantDeLaLigne(lancement.avec) !== null
    return unMontant ? CONSEIL_NON_ETABLI : null
}

/**
 * L'étude dont la ligne d'historique dit le résultat : celle du scénario
 * conseillé de la paire quand le conseil est ÉTABLI, l'étude seule sinon.
 * `null` pour une paire sans conseil établi. Le libellé d'état et le montant
 * de la ligne se lisent sur cette même étude (revue de nuit du 15/10/2026,
 * E1 : l'état de la jambe « sans » accompagnait le montant de la jambe
 * conseillée, « Rien à changer sur l'acheminement +223 € »).
 */
export function etudeDuResultat(
    lancement: LancementLu
): OptimTurpeV2Etude | null {
    if (lancement.sans && lancement.avec) {
        // §8.4.5 (C25) : le scénario écrit est celui du montant servi tant
        // que le scénario retenu n'est pas enregistré, puis le scénario
        // retenu, montant compris (lot 4g, `scenario_retenu` servi).
        const retenu = scenarioRetenuServi(lancement.sans, lancement.avec)
        if (retenu) return retenu === "avec" ? lancement.avec : lancement.sans
        const conseil = conseilEtabli(lancement.sans, lancement.avec)
        if (conseil === null) return null
        return conseil === "avec" ? lancement.avec : lancement.sans
    }
    return lancement.principale
}

/**
 * L'étude a-t-elle été produite par l'ANCIEN moteur (DEV-2311, F1) ?
 *
 * Le moteur actuel écrit sur toute étude terminée soit son calcul
 * (`outputs.calcul`, qui porte les versions et la date de calcul), soit la trace
 * de sa mise en file (`reprise`, y compris sur un refus), et le rejeu y pose un
 * bloc `cycle`. Une étude terminée qui ne porte AUCUN des trois vient de
 * l'ancien moteur. Le résumé que la liste du cycle adjoint à chaque étude sans
 * bloc (`presume: true`) ne compte pas : il est présumé pour toutes.
 *
 * Une étude en calcul n'est jamais classée : elle n'a encore rien écrit.
 */
export function estDeVersionAnterieure(etude: OptimTurpeV2Etude): boolean {
    if (etude.status !== "SUCCESS" && etude.status !== "FAILURE") return false
    if (etude.calcul) return false
    if (etude.reprise) return false
    const cycle = etude.cycle
    if (cycle && cycle.presume !== true) return false
    return true
}

/**
 * Le lancement que la page rouvre à l'ouverture (DEV-2156) : le plus récent dont
 * les études sont TERMINÉES, COURANTES (jamais remplacées par un rejeu, `courante`
 * est un champ, pas une date), non supprimées, et produites par le moteur actuel
 * (DEV-2311 : une étude de l'ancien moteur reste dans l'historique, marquée,
 * mais n'est plus servie comme le résultat du compteur). `null` s'il n'y en a
 * aucun : la page lance alors une étude.
 */
export function lancementAOuvrir(
    etudes: OptimTurpeV2Etude[]
): LancementLu | null {
    return grouperParLancement(etudesOuvrables(etudes))[0] ?? null
}

/**
 * Les études que la page peut rouvrir comme le résultat du compteur :
 * TERMINÉES, COURANTES, non supprimées, du moteur actuel. La règle
 * d'ouverture et « Voir la recommandation » (revue de nuit du 15/10/2026,
 * E15) la partagent.
 */
export function etudesOuvrables(
    etudes: OptimTurpeV2Etude[]
): OptimTurpeV2Etude[] {
    return etudes.filter((etude) => {
        const cycle = lireCycle(etude)
        return (
            (etude.status === "SUCCESS" || etude.status === "FAILURE") &&
            cycle.courante !== false &&
            cycle.remplaceePar === null &&
            cycle.supprimeeLe === null &&
            !estDeVersionAnterieure(etude)
        )
    })
}

/**
 * Le lancement dont le calcul est ENCORE EN FILE (DEV-2311, F3), ou `null`.
 *
 * Revenir sur la page pendant un calcul relançait une seconde paire, parce que
 * la règle d'ouverture ignore les études en attente. La page reprend désormais
 * le suivi de ce lancement. Une étude en attente depuis plus de
 * `SEUIL_ETUDE_ABANDONNEE_MS` n'attend plus rien (DEV-1621) : elle ne bloque
 * pas un lancement neuf. Un lancement dont un scénario a déjà abouti reste en
 * cours tant que l'autre attend.
 */
export function lancementEnCours(
    etudes: OptimTurpeV2Etude[],
    maintenant: number = Date.now(),
    seuilAbandonMs: number = SEUIL_ETUDE_ABANDONNEE_MS
): LancementLu | null {
    const visibles = etudes.filter((etude) => {
        const cycle = lireCycle(etude)
        return cycle.remplaceePar === null && cycle.supprimeeLe === null
    })
    const enAttente = (etude: OptimTurpeV2Etude): boolean => {
        if (etude.status === "SUCCESS" || etude.status === "FAILURE")
            return false
        const t = lireInstant(etude.created_at ?? null)
        return t !== null && maintenant - t < seuilAbandonMs
    }
    const creeLe = (etude: OptimTurpeV2Etude): number | null =>
        lireInstant(etude.created_at ?? null)
    // La plus récente des études en attente…
    const attente = visibles
        .filter(enAttente)
        .sort((a, b) => (creeLe(b) ?? 0) - (creeLe(a) ?? 0))[0]
    if (!attente) return null
    // … et sa jumelle, appariée sur la CRÉATION (les deux POST partent
    // ensemble) : la date de calcul d'un scénario déjà abouti peut s'écarter
    // de plus d'une minute quand la file est chargée.
    const scenario = scenarioDe(attente)
    const t = creeLe(attente)
    const jumelle =
        scenario === null || t === null
            ? undefined
            : visibles.find((autre) => {
                  if (autre === attente) return false
                  const s = scenarioDe(autre)
                  const ta = creeLe(autre)
                  return (
                      s !== null &&
                      s !== scenario &&
                      ta !== null &&
                      Math.abs(ta - t) <= ECART_MAX_SCENARIOS_MS &&
                      memePeriodeEtSource(attente, autre)
                  )
              })
    return construireLancement(jumelle ? [attente, jumelle] : [attente])
}

/**
 * L'étude est-elle EN ATTENTE DE CALCUL depuis plus de
 * `SEUIL_ETUDE_ABANDONNEE_MS` (DEV-2412) ? Non terminée, ni remplacée ni
 * supprimée, créée à une date lisible au delà du seuil. Elle ne bloque plus un
 * lancement neuf (`lancementEnCours`) et ne s'ouvre pas (`etudesOuvrables`),
 * mais elle ne disparaît pas pour autant : la liste des études la montre,
 * datée, avec « Relancer ».
 */
export function estEnAttenteAbandonnee(
    etude: OptimTurpeV2Etude,
    maintenant: number = Date.now(),
    seuilAbandonMs: number = SEUIL_ETUDE_ABANDONNEE_MS
): boolean {
    if (etude.status === "SUCCESS" || etude.status === "FAILURE") return false
    const cycle = lireCycle(etude)
    if (cycle.remplaceePar !== null || cycle.supprimeeLe !== null) return false
    const t = lireInstant(etude.created_at ?? null)
    return t !== null && maintenant - t >= seuilAbandonMs
}

/** Les lancements dont une étude attend son calcul depuis plus du seuil (DEV-2412). */
export function lancementsEnAttenteAbandonnes(
    etudes: OptimTurpeV2Etude[],
    maintenant: number = Date.now()
): LancementLu[] {
    return grouperParLancement(etudes).filter((lancement) =>
        lancement.etudes.some((e) => estEnAttenteAbandonnee(e, maintenant))
    )
}

/**
 * Le corps d'un lancement NEUF sur les mêmes réglages qu'une étude restée en
 * attente (DEV-2412, « Relancer »). Une étude non terminée ne se rejoue pas
 * (le moteur refuse `cycle-etude-non-terminee`) : on relance donc avec ses
 * entrées, la période seulement si ses deux bornes sont posées, sans
 * `allow_cmdps`, que le lancement pose lui-même par scénario.
 */
export function corpsDeRelance(etude: OptimTurpeV2Etude): OptimTurpeV2RunBody {
    const inputs = etude.inputs
    const corps: OptimTurpeV2RunBody = {}
    if (inputs?.data?.date_from && inputs.data.date_to) {
        corps.date_from = inputs.data.date_from
        corps.date_to = inputs.data.date_to
    }
    if (inputs?.comptage) corps.comptage = inputs.comptage
    if (inputs?.compteur) corps.compteur = inputs.compteur
    if (inputs?.structure_tarifaire) {
        corps.structure_tarifaire = inputs.structure_tarifaire
    }
    if (inputs?.optimisation) {
        const { allow_cmdps: _ignore, ...reste } = inputs.optimisation
        if (Object.keys(reste).length > 0) corps.optimisation = reste
    }
    return corps
}

/** Une écriture stable d'une valeur : les clés des objets triées. */
function ecritureStable(valeur: unknown): string {
    if (Array.isArray(valeur)) {
        return `[${valeur.map(ecritureStable).join(",")}]`
    }
    if (valeur !== null && typeof valeur === "object") {
        const entrees = Object.entries(valeur as Record<string, unknown>)
            .filter(([, v]) => v !== undefined)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        return `{${entrees
            .map(([k, v]) => `${JSON.stringify(k)}:${ecritureStable(v)}`)
            .join(",")}}`
    }
    return JSON.stringify(valeur ?? null)
}

/** Les réglages d'un lancement, tels que « Relancer » les renverrait. */
function reglagesDuLancement(lancement: LancementLu): string {
    return ecritureStable({
        source: lancement.principale.inputs?.source?._id_source ?? null,
        corps: corpsDeRelance(lancement.principale),
    })
}

/**
 * La RELANCE d'un lancement resté en attente (finitions après relecture de
 * DEV-2412) : le lancement le plus récent, créé APRÈS lui, sur les mêmes
 * réglages (même compteur, même corps de relance), non supprimé. `null` s'il
 * n'y en a pas.
 *
 * Tant qu'elle existe, l'étude en attente ne propose plus « Relancer » :
 * chaque clic créait un lancement de plus. La règle se lit sur la liste du
 * moteur, elle tient donc aussi après un rechargement de la page.
 */
export function relanceDuLancement(
    lancement: LancementLu,
    lancements: LancementLu[]
): LancementLu | null {
    const depuis = instantDeCreation(lancement.principale)
    if (depuis === null) return null
    const reglages = reglagesDuLancement(lancement)
    let relance: LancementLu | null = null
    let instantRelance = -Infinity
    for (const autre of lancements) {
        if (autre === lancement || autre.cle === lancement.cle) continue
        if (lireCycle(autre.principale).supprimeeLe !== null) continue
        const t = instantDeCreation(autre.principale)
        if (t === null || t <= depuis || t <= instantRelance) continue
        if (reglagesDuLancement(autre) !== reglages) continue
        relance = autre
        instantRelance = t
    }
    return relance
}

/**
 * La colonne « Origine » : le geste qui a créé l'étude, tel que le moteur le sert
 * (`creation`, `rejeu`, `scenario_libre`), et la date du dernier rétablissement.
 * Le moteur ne distingue pas une ouverture d'une relance depuis « Affiner » :
 * la page ne l'invente pas.
 */
export function lireOrigine(etude: OptimTurpeV2Etude): {
    origine: CycleLu["origine"]
    retablieLe: string | null
} {
    const cycle = lireCycle(etude)
    const retours = (etude.cycle?.retours ?? []).filter(
        (r) => !r.vers || r.vers === etude._id
    )
    const dernier = retours.length > 0 ? retours[retours.length - 1] : null
    return { origine: cycle.origine, retablieLe: dernier?.le ?? null }
}

/**
 * Les entrées d'une relance « Affiner l'étude », traduites en SURCHARGES de
 * rejeu (fragment d'`inputs`, DEV-2150). La période passe sous `data` ;
 * `_id_building` n'est pas une entrée ; `allow_cmdps` n'est jamais surchargé,
 * chaque scénario de la paire garde le sien.
 *
 * `fenetreParDefaut` (DEV-2178) : « Relancer l'étude sur les données du jour »
 * ne fabrique aucune fenêtre. Les deux bornes partent EXPLICITEMENT nulles :
 * le moteur écrit une feuille nulle telle quelle (`cycle.fusionner`), puis
 * recompose son défaut, 365 jours finissant à la dernière donnée détenue
 * (`calcul_tache.completer_fenetre`). Sans elles, le rejeu hériterait de la
 * période de l'étude rejouée.
 */
export function surchargesDeRejeu(
    corps: {
        date_from?: string
        date_to?: string
        _id_building?: string
        comptage?: unknown
        compteur?: unknown
        structure_tarifaire?: unknown
        optimisation?: Record<string, unknown>
    },
    { fenetreParDefaut = false }: { fenetreParDefaut?: boolean } = {}
): Record<string, unknown> {
    const surcharges: Record<string, unknown> = {}
    if (fenetreParDefaut) {
        surcharges.data = { date_from: null, date_to: null }
    } else if (corps.date_from || corps.date_to) {
        surcharges.data = {
            ...(corps.date_from ? { date_from: corps.date_from } : {}),
            ...(corps.date_to ? { date_to: corps.date_to } : {}),
        }
    }
    if (corps.comptage) surcharges.comptage = corps.comptage
    if (corps.compteur) surcharges.compteur = corps.compteur
    if (corps.structure_tarifaire) {
        surcharges.structure_tarifaire = corps.structure_tarifaire
    }
    if (corps.optimisation) {
        const { allow_cmdps: _ignore, ...reste } = corps.optimisation
        if (Object.keys(reste).length > 0) surcharges.optimisation = reste
    }
    return surcharges
}

/**
 * Les études affichées se REJOUENT-elles (DEV-2150) ? Oui quand elles sont
 * terminées, courantes et jamais remplacées : le moteur refuse sinon
 * (`cycle-etude-non-terminee`, `cycle-etude-deja-remplacee`).
 */
export function idsRejouables(
    etudes: Array<OptimTurpeV2Etude | null | undefined>
): string[] {
    const presentes = etudes.filter(
        (e, i, arr): e is OptimTurpeV2Etude =>
            Boolean(e?._id) && arr.findIndex((x) => x?._id === e?._id) === i
    )
    if (presentes.length === 0) return []
    const toutes = presentes.every((etude) => {
        const cycle = lireCycle(etude)
        return (
            (etude.status === "SUCCESS" || etude.status === "FAILURE") &&
            cycle.courante !== false &&
            cycle.remplaceePar === null &&
            cycle.supprimeeLe === null
        )
    })
    return toutes ? presentes.map((e) => String(e._id)) : []
}

/** Le scénario servi par le moteur (`restitution.scenario`), ou `null`. */
export function scenarioServi(
    etude: OptimTurpeV2Etude
): "sans_depassements" | "avec_depassements" | "scenario_libre" | null {
    const s = etude.scenario
    return s === "sans_depassements" ||
        s === "avec_depassements" ||
        s === "scenario_libre"
        ? s
        : null
}

// =============================================================================
// DEV-2423, contrat de l'interface v1.2 §8.4.10 et §8.4.11 (C34) : la règle
// d'affichage de l'historique, calculée par la page.
//
// Ce que le client doit vivre : une seule étude recommandée est « la »
// recommandation du compteur ; une relance depuis le formulaire laisse
// l'ancienne lisible, marquée « Remplacée le JJ/MM/AAAA à hh:mm » ; quatre
// relances identiques ne sont pas quatre résultats, elles tiennent sur une
// ligne avec le nombre de lancements. Aujourd'hui `courante` et
// `remplacee_par` ne sont servis que par un rejeu : la page déduit le reste,
// sans rien écrire en base.
// =============================================================================

/** Un lancement terminé dont la recommandation peut être « en vigueur » (§8.4.10). */
function estRecommandationTerminee(lancement: LancementLu): boolean {
    return (
        lancement.etudes.some((e) => e.status === "SUCCESS") &&
        !lancement.etudes.some(estScenarioLibre) &&
        !lancement.etudes.some(estDeVersionAnterieure) &&
        !lancement.etudes.some(estSupprimee)
    )
}

/**
 * L'instant où le lancement a pris la main : son calcul, ou son dernier
 * rétablissement s'il est plus récent.
 */
function instantDePriseDeMain(lancement: LancementLu): number | null {
    const calcul = lireInstant(lancement.date)
    const retablie = lancement.etudes
        .map((e) => lireInstant(lireOrigine(e).retablieLe))
        .filter((t): t is number => t !== null)
    const tous = [calcul, ...retablie].filter((t): t is number => t !== null)
    return tous.length ? Math.max(...tous) : null
}

/**
 * La recommandation en vigueur (§8.4.10) : le lancement recommandé terminé qui
 * a pris la main le plus récemment (calcul ou rétablissement). Les études
 * saisies ne remplacent rien. `null` sans recommandation terminée.
 */
export function lancementEnVigueur(
    lancements: LancementLu[]
): LancementLu | null {
    let meilleur: LancementLu | null = null
    let instantMeilleur = -Infinity
    for (const lancement of lancements) {
        if (!estRecommandationTerminee(lancement)) continue
        const t = instantDePriseDeMain(lancement) ?? -Infinity
        if (meilleur === null || t > instantMeilleur) {
            meilleur = lancement
            instantMeilleur = t
        }
    }
    return meilleur
}

/**
 * La date (ISO) à laquelle une recommandation a été remplacée (§8.4.10), ou
 * `null` pour la recommandation en vigueur, une étude saisie ou un calcul non
 * terminé. Le champ servi (`cycle.remplacee_le`) passe d'abord ; sinon,
 * l'instant où la recommandation suivante a pris la main.
 */
export function remplaceeLeDuLancement(
    lancement: LancementLu,
    lancements: LancementLu[]
): string | null {
    const servie = lancement.etudes
        .map((e) => lireCycle(e).remplaceeLe)
        .find((d): d is string => d !== null)
    if (servie) return servie
    if (!estRecommandationTerminee(lancement)) return null
    if (lancementEnVigueur(lancements) === lancement) return null
    const depuis = instantDePriseDeMain(lancement)
    if (depuis === null) return null
    let suivant: number | null = null
    for (const autre of lancements) {
        if (autre === lancement || !estRecommandationTerminee(autre)) continue
        const t = instantDePriseDeMain(autre)
        if (t === null || t <= depuis) continue
        if (suivant === null || t < suivant) suivant = t
    }
    return suivant === null ? null : new Date(suivant).toISOString()
}

/** Le résultat servi d'un lancement, écrit pour comparer deux relances (§8.4.11). */
function resultatComparable(lancement: LancementLu): string {
    return ecritureStable({
        montant: resultatsDuLancement(lancement),
        etudes: lancement.etudes.map((e) => ({
            status: e.status ?? null,
            etat: e.etat ?? null,
            nette: e.economie?.nette_eur ?? null,
            meilleure: e.meilleure?.structure_tarifaire ?? null,
            reserves: (e.reserves ?? []).map((r) => r?.code ?? null).sort(),
        })),
    })
}

/** Les entrées d'un lancement, hors scénario (§8.4.11 : mêmes hypothèses). */
function entreesDuLancement(lancement: LancementLu): string {
    return lancement.etudes.map(entreesHorsScenario).sort().join("|")
}

/** Deux lancements sont-ils des relances identiques (§8.4.11) ? */
function sontIdentiques(a: LancementLu, b: LancementLu): boolean {
    const termine = (l: LancementLu) =>
        l.etudes.every((e) => e.status === "SUCCESS")
    const retabli = (l: LancementLu) =>
        l.etudes.some((e) => lireOrigine(e).retablieLe !== null)
    // Une étude de l'ancien moteur, remplacée ou supprimée par un geste servi
    // garde sa ligne : sa marque se lit sur elle seule.
    const marquee = (l: LancementLu) =>
        l.etudes.some(
            (e) =>
                estDeVersionAnterieure(e) ||
                estSupprimee(e) ||
                lireCycle(e).remplaceePar !== null ||
                lireCycle(e).remplaceeLe !== null
        )
    return (
        termine(a) &&
        termine(b) &&
        !retabli(a) &&
        !retabli(b) &&
        !marquee(a) &&
        !marquee(b) &&
        a.etudes.length === b.etudes.length &&
        estScenarioLibre(a.principale) === estScenarioLibre(b.principale) &&
        entreesDuLancement(a) === entreesDuLancement(b) &&
        resultatComparable(a) === resultatComparable(b)
    )
}

/** Une ligne de l'historique : un lancement, ou des relances identiques regroupées. */
export type LigneDHistorique = {
    /** Le lancement le plus récent du groupe : il porte la date, la référence et les gestes. */
    lancement: LancementLu
    /** Les lancements de la ligne, du plus récent au plus ancien (au moins un). */
    regroupes: LancementLu[]
}

/**
 * Les lignes de l'historique (§8.4.11) : des lancements qui se suivent, avec
 * les mêmes entrées hors scénario, le même objectif (recommandation ou étude
 * saisie) et le même résultat servi, sans rétablissement entre eux, tiennent
 * sur une ligne. L'étude affichée n'est jamais cachée dans un groupe : elle
 * en est la plus récente, ou elle reste seule.
 */
export function lignesDHistorique(
    lancements: LancementLu[],
    couranteId: string | null = null
): LigneDHistorique[] {
    const estAffichee = (l: LancementLu) =>
        couranteId !== null && l.ids.includes(couranteId)
    const lignes: LigneDHistorique[] = []
    for (const lancement of lancements) {
        const derniere = lignes[lignes.length - 1]
        const plusAncien = derniere?.regroupes[derniere.regroupes.length - 1]
        if (
            derniere &&
            plusAncien &&
            !estAffichee(lancement) &&
            sontIdentiques(plusAncien, lancement)
        ) {
            derniere.regroupes.push(lancement)
            continue
        }
        lignes.push({ lancement, regroupes: [lancement] })
    }
    return lignes
}

/**
 * « 4 études identiques (13:16 → 13:56) » (§8.4.11), ou `null` pour une ligne
 * d'un seul lancement. Les heures sont celles de Paris ; sur deux jours, la
 * date les précède.
 */
export function libelleDuRegroupement(
    ligne: LigneDHistorique,
    ecrire: (iso: string | null) => string
): string | null {
    const n = ligne.regroupes.length
    if (n < 2) return null
    const recent = ecrire(ligne.regroupes[0].date)
    const ancien = ecrire(ligne.regroupes[n - 1].date)
    const [jourRecent, heureRecente] = recent.split(" à ")
    const [jourAncien, heureAncienne] = ancien.split(" à ")
    const memeJour = jourRecent === jourAncien && heureRecente && heureAncienne
    const bornes = memeJour
        ? `${heureAncienne} → ${heureRecente}`
        : `${ancien} → ${recent}`
    return `${n} études identiques (${bornes})`
}

// =============================================================================
// DEV-2439 : une relance refusée ne fait pas perdre la recommandation.
//
// Ce que le client doit vivre : il relance une recommandation depuis « Modifier
// les hypothèses », le moteur refuse (aucune mesure sur la période) ; la
// recommandation d'avant n'est pas perdue. « Voir la recommandation » la
// rouvre, ou la rétablit si un refus l'a marquée remplacée, et ne lance JAMAIS
// une étude neuve tant que le compteur a une étude aboutie du moteur actuel.
// Jusqu'au correctif du moteur, un rejeu refusé marquait l'étude d'avant
// remplacée : ces études existent en base, la page les rétablit.
// =============================================================================

/** Le geste qui rend au client sa recommandation, sur un lancement. */
export type RecommandationARendre = {
    lancement: LancementLu
    /** `ouvrir` : relire sans recalcul ; `retablir` : la remettre courante. */
    geste: "ouvrir" | "retablir"
}

/** Le lancement porte-t-il une étude remplacée, ou qui n'est plus courante ? */
export function lancementRemplace(lancement: LancementLu): boolean {
    return lancement.etudes.some((e) => {
        const cycle = lireCycle(e)
        return cycle.remplaceePar !== null || cycle.courante === false
    })
}

/**
 * La recommandation à rendre au client, ou `null` quand le compteur n'en a
 * aucune d'aboutie (moteur actuel, non supprimée, hors « Vos puissances »).
 *
 * D'abord la plus récente des recommandations COURANTES (la règle d'ouverture
 * de la page, E15) ; à défaut, la recommandation en vigueur au sens de
 * l'historique (§8.4.10), remplacée comprise : elle se rétablit.
 */
export function recommandationARendre(
    etudes: OptimTurpeV2Etude[]
): RecommandationARendre | null {
    const courante = grouperParLancement(etudesOuvrables(etudes)).find(
        (lancement) =>
            lancement.principale.status === "SUCCESS" &&
            !estScenarioLibre(lancement.principale)
    )
    if (courante) return { lancement: courante, geste: "ouvrir" }
    const enVigueur = lancementEnVigueur(grouperParLancement(etudes))
    if (!enVigueur) return null
    return {
        lancement: enVigueur,
        geste: lancementRemplace(enVigueur) ? "retablir" : "ouvrir",
    }
}

/**
 * Le lancement que la page ouvre à l'ARRIVÉE sur un compteur (contrat v1.6
 * §8.4.14, décision (q) du 20/10/2026) : arriver sur un compteur, c'est
 * retrouver sa recommandation.
 *
 * - Le dernier lancement ouvrable refusé pour une raison indépendante du
 *   scénario (compteur résilié, collecte interrompue) reste ouvert : le
 *   dernier fait connu sur le compteur passe avant une recommandation.
 * - Sinon, la règle de « Voir la recommandation » (`recommandationARendre`),
 *   au seul geste « ouvrir » : la recommandation courante la plus récente qui
 *   a abouti, hors « Vos puissances », sinon celle en vigueur (8.4.10) si elle
 *   s'ouvre sans rétablissement.
 * - Sinon, la règle d'avant (`lancementAOuvrir`). Une recommandation
 *   remplacée (geste « retablir ») ne s'ouvre jamais ici : l'arrivée n'écrit
 *   rien (A12) ; le rétablissement reste le geste du client.
 */
export function lancementAOuvrirALArrivee(
    etudes: OptimTurpeV2Etude[]
): LancementLu | null {
    const dernier = lancementAOuvrir(etudes)
    if (dernier && refusConnuDe(dernier.etudes)) return dernier
    const aRendre = recommandationARendre(etudes)
    if (aRendre?.geste === "ouvrir") {
        const ouvrables = new Set(etudesOuvrables(etudes).map((e) => e._id))
        if (aRendre.lancement.etudes.every((e) => ouvrables.has(e._id)))
            return aRendre.lancement
    }
    return dernier
}

/**
 * Le compteur a-t-il une étude ABOUTIE du moteur actuel, non supprimée
 * (recommandation ou « Vos puissances ») ? Tant que oui, « Voir la
 * recommandation » ne lance pas d'étude neuve.
 */
export function aUneEtudeAboutie(etudes: OptimTurpeV2Etude[]): boolean {
    return etudes.some(
        (e) =>
            e.status === "SUCCESS" &&
            !estSupprimee(e) &&
            !estDeVersionAnterieure(e)
    )
}
